import {
  Box,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  makeStyles,
  styled
} from "@material-ui/core";
import { Search } from "shared/Search";

export const DashboardPanel = styled(Box)(({ theme }) => ({
  minHeight: "100%",
  background: "#FFF",
  maxWidth: "75rem",
  margin: "0 auto",
  padding: "1.25rem 1.5rem 1.5rem",
  [theme.breakpoints.down("sm")]: {
    padding: "1rem"
  }
}));

export const DashboardHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.75rem",
  flexWrap: "wrap",
  marginBottom: "1.125rem",
  [theme.breakpoints.down("xs")]: {
    alignItems: "stretch"
  }
}));

export const DashboardTitle = styled(Typography)(({ theme }) => ({
  margin: 0,
  color: "#111827",
  fontFamily: "Inter",
  fontSize: "1.125rem",
  fontWeight: 700,
  lineHeight: 1.25,
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.25rem"
  }
}));

export const MainContent = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateAreas: `
    'cards cards'
    'storage side'
    'batches side'
  `,
  gridTemplateColumns: "minmax(0, 1fr) 17.5rem",
  alignItems: "start",
  gap: "1rem",
  [theme.breakpoints.down("md")]: {
    gridTemplateAreas: `
      'cards'
      'side'
      'storage'
      'batches'
    `,
    gridTemplateColumns: "1fr"
  }
}));

export const SectionTitle = styled(Typography)({
  color: "#111827",
  fontFamily: "Inter",
  fontSize: "0.9375rem",
  fontWeight: 700,
  lineHeight: 1.3,
  wordWrap: "break-word"
});

export const Content = styled(Box)({
  display: "flex",
  minWidth: 0,
  flexDirection: "column"
});

export const CustomTableContainer = styled(TableContainer)(({ theme }) => ({
  display: "flex",
  flex: 1,
  minWidth: 0,
  flexDirection: "column",
  background: "#FFF",
  border: "1px solid #E5E7EB",
  borderRadius: "0.5rem",
  boxShadow: "0 6px 16px rgba(17, 24, 39, 0.035)",
  overflow: "hidden",
  padding: "1.25rem",
  [theme.breakpoints.down("sm")]: {
    padding: "1.25rem"
  }
}));

export const TablePanelHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.75rem",
  marginBottom: "1rem",
  [theme.breakpoints.down("sm")]: {
    alignItems: "stretch",
    flexDirection: "column"
  }
}));

export const SearchContent = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.75rem",
  marginBottom: "1rem",
  [theme.breakpoints.down("sm")]: {
    alignItems: "stretch",
    flexDirection: "column"
  }
}));

export const ButtonReportContent = styled(Box)(({ theme }) => ({
  width: "9rem",
  flexShrink: 0,
  [theme.breakpoints.down("sm")]: {
    width: "100%"
  }
}));

export const SearchInput = styled(Search)(({ theme }) => ({
  width: "100%",
  maxWidth: "16.875rem",
  "& .MuiOutlinedInput-root": {
    height: "2.25rem",
    borderRadius: "0.375rem",
    background: "#FFF"
  },
  "& .MuiOutlinedInput-input": {
    color: "#111827",
    fontFamily: "Inter",
    fontSize: "0.75rem"
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#D1D5DB"
  },
  [theme.breakpoints.down("sm")]: {
    maxWidth: "100%"
  }
}));

export const TableContent = styled(TableContainer)({
  display: "flex",
  flexDirection: "column",
  maxHeight: "18.75rem",
  overflowY: "auto",
  border: "1px solid #E5E7EB",
  borderRadius: "0.5rem",
  boxShadow: "none",
  "&::-webkit-scrollbar": {
    width: "10px",
    background: "transparent"
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#A1CA4F",
    borderRadius: "19px"
  }
});

export const StyledTableHead = styled(TableHead)({
  background: "#F3F8E6"
});

export const HeadCell = styled(TableCell)({
  background: "#F3F8E6",
  color: "#111827",
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  fontSize: "0.75rem",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "1rem",
  padding: "0.875rem 1.75rem"
});

export const HeadSortLabel = styled(TableSortLabel)({
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  fontSize: "0.75rem",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "1rem",
  textTransform: "none",
  padding: 0,
  color: "inherit",
  "&:hover": {
    color: "inherit"
  },
  "&.MuiTableSortLabel-active": {
    color: "inherit"
  },
  "& .MuiTableSortLabel-icon": {
    fontSize: "1rem"
  }
});

export const BodyRow = styled(TableRow)({
  "&:hover": {
    backgroundColor: "#FAFAFA"
  },
  "&:last-child td": {
    borderBottom: 0
  }
});

export const BodyCell = styled(TableCell)({
  color: "#111827",
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  fontSize: "0.75rem",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "1rem",
  padding: "0.875rem 1.75rem",
  borderBottom: "1px solid #E5E7EB"
});

export const BatchTableContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  background: "#fafafa",
  margin: "0",
  borderRadius: "0.5rem",
  padding: "0.75rem",
  minHeight: "18rem",
  maxHeight: "18rem",
  overflowY: "scroll",
  "&::-webkit-scrollbar": {
    width: "10px",
    background: "transparent"
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#A1CA4F",
    borderRadius: "19px"
  }
});

export const BatchTableContent = styled(Box)({
  display: "grid",
  gridTemplateColumns:
    "minmax(6rem, 9rem) 0.8fr minmax(11rem, 1.5fr) 0.6fr minmax(5.5rem, 0.9fr) minmax(6.5rem, 0.9fr) minmax(3.5rem, 0.6fr)",
  rowGap: "0.375rem",
  padding: "0.75rem"
});

export const BatchTitle = styled(Typography)({
  display: "flex",
  color: "#757575",
  fontFamily: "Roboto",
  fontSize: "0.75rem",
  fontWeight: 500,
  justifyContent: "center",
  textAlign: "center",
  padding: "0.4rem 0.5rem",
  wordWrap: "break-word"
});

export const BatchDueInfo = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
});

export const BatchInfo = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#FFF",
  color: "#353535",
  fontFamily: "Roboto",
  fontSize: "0.75rem",
  fontWeight: 400,
  padding: "0.35rem 0.75rem",
  wordWrap: "break-word"
});

// --- Tabela densa "Produtos perto do vencimento" (desktop) ---
export const DueTableContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  background: "#FFF",
  border: "1px solid #E5E7EB",
  borderRadius: "0.5rem",
  overflowY: "auto",
  maxHeight: "30rem",
  "&::-webkit-scrollbar": {
    width: "10px",
    background: "transparent"
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#A1CA4F",
    borderRadius: "19px"
  }
});

export const DueTable = styled(Box)({
  display: "grid",
  gridTemplateColumns:
    "minmax(8rem, 9rem) minmax(4.5rem, 6rem) minmax(9rem, 1fr) minmax(6.5rem, 8rem) minmax(4rem, 5rem) minmax(5rem, 6rem) 3.25rem",
  alignItems: "stretch"
});

export const DueHeadCell = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "0.25rem",
  position: "sticky",
  top: 0,
  zIndex: 1,
  background: "#F3F8E6",
  color: "#6B7280",
  fontFamily: "Roboto",
  fontSize: "0.6875rem",
  fontWeight: 700,
  letterSpacing: "0.03em",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  borderBottom: "1px solid #E5E7EB",
  padding: "0 0.75rem",
  height: "2.5rem"
});

export const DueCell = styled(Box)({
  display: "flex",
  alignItems: "center",
  minHeight: "3.5rem",
  background: "#FFF",
  color: "#353535",
  fontFamily: "Roboto",
  fontSize: "0.75rem",
  fontWeight: 400,
  borderBottom: "1px solid #EEF0F2",
  padding: "0.5rem 0.75rem",
  wordBreak: "break-word"
});

export const DueBadge = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  padding: "0.2rem 0.55rem",
  borderRadius: "999px",
  fontFamily: "Roboto",
  fontSize: "0.6875rem",
  fontWeight: 600,
  lineHeight: 1,
  whiteSpace: "nowrap"
});

export const ClassBadge = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "1.5rem",
  padding: "0.15rem 0.4rem",
  borderRadius: "0.375rem",
  background: "#EEF1F4",
  color: "#374151",
  fontFamily: "Roboto",
  fontSize: "0.75rem",
  fontWeight: 700,
  lineHeight: 1.2
});

// --- Tabela agrupada por produto "Lotes perto do vencimento" (desktop) ---
// Visualmente uma unica tabela continua (como "Produtos com saldo negativo"),
// sem cards/gaps entre os grupos; cada linha expande pra mostrar os lotes.
// Reaproveita o mesmo container/scroll de DueTableContainer pra nao duplicar
// a definicao (mesma borda, raio, scrollbar e altura maxima).
export const GroupListContainer = styled(DueTableContainer)({});

export const GroupRow = styled(Box)({
  display: "flex",
  flexDirection: "column",
  background: "#FFF",
  borderBottom: "1px solid #EEF0F2",
  flexShrink: 0
});

export const GroupHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.75rem",
  minHeight: "3rem",
  padding: "0.5rem 1rem",
  cursor: "pointer",
  flexWrap: "wrap",
  userSelect: "none",
  "&:hover": {
    background: "#FAFAFA"
  }
});

export const GroupHeaderLeft = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "0.625rem",
  minWidth: 0
});

export const GroupChevronButton = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  width: "1.75rem",
  height: "1.75rem",
  borderRadius: "0.375rem",
  background: "#F3F8E6",
  border: "1px solid #CFE3A0",
  color: "#557A0E"
});

export const GroupProductName = styled(Typography)({
  color: "#111827",
  fontFamily: "Inter",
  fontSize: "0.9375rem",
  fontWeight: 700,
  lineHeight: 1.3,
  wordBreak: "break-word"
});

export const GroupHeaderMeta = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  flexWrap: "wrap"
});

export const GroupMetaText = styled(Typography)({
  color: "#4B5563",
  fontFamily: "Roboto",
  fontSize: "0.75rem",
  fontWeight: 500,
  whiteSpace: "nowrap"
});

export const GroupMetaDot = styled("span")({
  color: "#D1D5DB"
});

// Reaproveita o grid de DueTable (mesmo display/alignItems), so troca as
// colunas (sem "Produto", que aqui vira o cabecalho do grupo) e adiciona a
// borda superior que separa a linha expandida do cabecalho do grupo.
export const GroupDueTable = styled(DueTable)({
  gridTemplateColumns:
    "minmax(8rem, 9rem) minmax(4.5rem, 6rem) minmax(10rem, 1fr) minmax(4rem, 5rem) minmax(5rem, 6rem) 3.25rem",
  borderTop: "1px solid #E5E7EB"
});

export const CountBadge = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  background: "#F3F8E6",
  color: "#557A0E",
  fontFamily: "Inter",
  fontSize: "0.6875rem",
  fontWeight: 700,
  borderRadius: "999px",
  padding: "0.15rem 0.6rem",
  lineHeight: 1.2,
  whiteSpace: "nowrap"
});

export const useStyles = makeStyles(theme => ({
  button: {
    display: "flex",
    alignItems: "center",
    gap: "0.375rem",
    minHeight: "2.4rem",
    borderRadius: "0.375rem",
    padding: "0.5rem 1rem",
    fontFamily: "Inter",
    fontSize: "0.75rem",
    fontWeight: 700,
    letterSpacing: 0
  },
  reportButton: {
    minHeight: "2.25rem",
    borderRadius: "0.375rem",
    borderColor: theme.palette.secondary.main,
    color: theme.palette.secondary.main,
    padding: "0 1rem",
    "& .MuiTypography-root": {
      color: `${theme.palette.secondary.main} !important`,
      fontFamily: "Inter",
      fontSize: "0.6875rem",
      fontWeight: 700,
      letterSpacing: 0,
      whiteSpace: "nowrap"
    },
    "&:hover": {
      borderColor: theme.palette.secondary.main,
      background: "rgba(65, 109, 11, 0.08)"
    },
    "&.Mui-disabled": {
      borderColor: "#D1D5DB",
      color: "#9CA3AF"
    },
    "&.Mui-disabled .MuiTypography-root": {
      color: "#9CA3AF !important"
    }
  }
}));

export const ConfirmPendingAlert = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "0.875rem",
  background: "#FFF4E5",
  border: "1px solid #FFD099",
  borderLeft: "0.3rem solid #FB8C00",
  borderRadius: "0.5rem",
  padding: "0.75rem 1.25rem",
  marginBottom: "0.75rem"
});

export const ConfirmPendingAlertIconBox = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  width: "2.45rem",
  height: "2.45rem",
  background: "#FFE0B2",
  borderRadius: "0.5rem"
});

export const ConfirmPendingAlertInfo = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "0.15rem"
});

export const ConfirmPendingAlertLabel = styled(Typography)({
  color: "#7C4700",
  fontFamily: "Inter",
  fontSize: "0.75rem",
  fontWeight: 500,
  lineHeight: 1.25
});

export const ConfirmPendingAlertCount = styled(Typography)({
  color: "#E65100",
  fontFamily: "Inter",
  fontSize: "1.25rem",
  fontWeight: 800,
  lineHeight: 1
});

// --- Cards para visualizacao mobile (abaixo de xs) ---
export const MobileCardList = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "0.625rem",
  padding: "0.25rem"
});

export const MobileCard = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
  background: "#FFF",
  border: "1px solid #EEF0F2",
  borderRadius: "0.5rem",
  boxShadow: "0 1px 3px rgba(17, 24, 39, 0.08)",
  padding: "0.875rem"
});

export const MobileCardHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.625rem"
});

export const MobileCardName = styled(Typography)({
  flex: 1,
  minWidth: 0,
  color: "#111827",
  fontFamily: "Roboto",
  fontSize: "0.875rem",
  fontWeight: 600,
  lineHeight: 1.3,
  wordBreak: "break-word"
});

export const MobileCardId = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  flexShrink: 0,
  background: "#F3F4F6",
  color: "#6B7280",
  fontFamily: "Roboto",
  fontSize: "0.6875rem",
  fontWeight: 600,
  borderRadius: "0.375rem",
  padding: "0.125rem 0.5rem"
});

export const MobileCardRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.75rem"
});

export const MobileCardLabel = styled(Typography)({
  color: "#6B7280",
  fontFamily: "Roboto",
  fontSize: "0.75rem",
  fontWeight: 500
});

export const MobileCardValue = styled(Typography)({
  color: "#111827",
  fontFamily: "Roboto",
  fontSize: "0.8125rem",
  fontWeight: 600,
  textAlign: "right"
});

// --- Card de vencimento estilo 'opcao 3' (mobile) ---
export const BatchStatusCard = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "0.375rem",
  background: "#FFF",
  border: "1px solid #EEF0F2",
  borderLeft: "4px solid #EB001B",
  borderRadius: "0.5rem",
  boxShadow: "0 1px 3px rgba(17, 24, 39, 0.08)",
  padding: "0.625rem 0.75rem"
});

export const MobileBadgeRow = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "0.375rem"
});

export const MobileQtyBadge = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  background: "#EEF1F4",
  color: "#374151",
  fontFamily: "Roboto",
  fontSize: "0.6875rem",
  fontWeight: 600,
  borderRadius: "999px",
  padding: "0.2rem 0.55rem",
  lineHeight: 1,
  whiteSpace: "nowrap"
});

export const MobileMetaLine = styled(Typography)({
  color: "#6B7280",
  fontFamily: "Roboto",
  fontSize: "0.6875rem",
  fontWeight: 500,
  lineHeight: 1.3,
  wordBreak: "break-word"
});

export const BatchStatusName = styled(Typography)({
  color: "#111827",
  fontFamily: "Roboto",
  fontSize: "0.9375rem",
  fontWeight: 600,
  lineHeight: 1.25,
  wordBreak: "break-word"
});

export const BatchStatusText = styled(Typography)({
  fontFamily: "Roboto",
  fontSize: "0.75rem",
  fontWeight: 500,
  lineHeight: 1.3,
  marginTop: "0.125rem"
});

export const BatchStatusGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  rowGap: "0.625rem",
  borderTop: "1px solid #F1F1F1",
  paddingTop: "0.625rem"
});

export const BatchStatusCol = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "0.125rem",
  padding: "0 0.25rem",
  "&:nth-of-type(odd)": {
    borderRight: "1px solid #F1F1F1"
  }
});

export const BatchStatusColLabel = styled(Typography)({
  color: "#6B7280",
  fontFamily: "Roboto",
  fontSize: "0.6875rem",
  fontWeight: 500
});

export const BatchStatusColValue = styled(Typography)({
  color: "#111827",
  fontFamily: "Roboto",
  fontSize: "0.8125rem",
  fontWeight: 600
});
